import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Play, Pause, Volume2, VolumeX, Maximize, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { VideoPlayerProps } from "@/types";
import logoSrc from "../../maherLogo.png";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

interface ProtectedVideoPlayerProps extends VideoPlayerProps {
  strengthName?: string;
  onComplete?: () => void;
  initialProgress?: number;
  enableProtection?: boolean;
}

export const ProtectedVideoPlayer = ({
  url,
  videoUrl: videoUrlProp,
  title: strengthName = '',
  onComplete,
  initialProgress = 0,
  autoPlay = false,
  controls: showControls = true,
  enableProtection = true,
}: ProtectedVideoPlayerProps) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);
  const playPromiseRef = useRef<Promise<void> | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(initialProgress);
  const [hasWatched, setHasWatched] = useState(initialProgress >= 90);
  const [securityAlert, setSecurityAlert] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Use either url or videoUrl prop
  const videoUrl = url || videoUrlProp;

  // Force HTTPS for video URLs to prevent mixed content errors
  const secureVideoUrl = videoUrl?.replace(/^http:\/\//i, 'https://');

  // Fetch video with authentication and create blob URL to prevent download managers
  useEffect(() => {
    if (!secureVideoUrl || !enableProtection) {
      setBlobUrl(secureVideoUrl || null);
      setIsLoading(false);
      return;
    }

    let objectUrl: string | null = null;

    const fetchProtectedVideo = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);

        // Get authentication token
        const token = localStorage.getItem('token');
        
        // Fetch video with authentication headers
        const response = await fetch(secureVideoUrl, {
          method: 'GET',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'X-Video-Access': 'protected',
            'Cache-Control': 'no-store',
          },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Failed to load video: ${response.status}`);
        }

        // Convert to blob
        const blob = await response.blob();
        
        // Create blob URL (this hides the original URL from extensions)
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading protected video:', error);
        setLoadError(getLocalizedErrorMessage(error, 'errors.videoLoadFailed'));
        setIsLoading(false);
        // Fallback to direct URL if fetch fails (for development)
        if (secureVideoUrl.includes('commondatastorage') || secureVideoUrl.includes('localhost')) {
          setBlobUrl(secureVideoUrl);
        }
      }
    };

    fetchProtectedVideo();

    // Cleanup: revoke blob URL when component unmounts
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [secureVideoUrl, enableProtection]);

  // Anti-scraping protection: Detect DevTools (only when video is playing)
  useEffect(() => {
    if (!enableProtection) return;

    const detectDevTools = () => {
      const threshold = 200;
      const widthDiff = window.outerWidth - window.innerWidth;
      const heightDiff = window.outerHeight - window.innerHeight;
      
      // Only trigger if DevTools is clearly open AND video is actively playing
      if ((widthDiff > threshold && heightDiff > threshold) || 
          (widthDiff > threshold * 1.5 || heightDiff > threshold * 1.5)) {
        if (videoRef.current && !videoRef.current.paused) {
          setSecurityAlert(true);
          videoRef.current.pause();
        }
      } else {
        // Clear alert if DevTools is closed
        setSecurityAlert(false);
      }
    };

    // Check periodically
    const interval = setInterval(detectDevTools, 2000);

    return () => clearInterval(interval);
  }, [enableProtection]);

  // Prevent right-click and context menu
  useEffect(() => {
    if (!enableProtection || !containerRef.current) return;

    const preventContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    const container = containerRef.current;
    container.addEventListener('contextmenu', preventContextMenu);

    return () => {
      container.removeEventListener('contextmenu', preventContextMenu);
    };
  }, [enableProtection]);

  // Prevent keyboard shortcuts for downloading
  useEffect(() => {
    if (!enableProtection) return;

    const preventShortcuts = (e: KeyboardEvent) => {
      // Prevent Ctrl+S (Save), Ctrl+Shift+I (DevTools), F12 (DevTools)
      if (
        (e.ctrlKey && e.key === 's') ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        e.key === 'F12'
      ) {
        e.preventDefault();
        setSecurityAlert(true);
        return false;
      }
    };

    window.addEventListener('keydown', preventShortcuts);

    return () => {
      window.removeEventListener('keydown', preventShortcuts);
    };
  }, [enableProtection]);

  // Prevent drag and drop
  useEffect(() => {
    if (!enableProtection || !videoRef.current) return;

    const preventDrag = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    const video = videoRef.current;
    video.addEventListener('dragstart', preventDrag);

    return () => {
      video.removeEventListener('dragstart', preventDrag);
    };
  }, [enableProtection]);

  // Add watermark overlay with logo
  useEffect(() => {
    if (!enableProtection || !containerRef.current) return;

    const watermark = document.createElement('img');
    watermark.src = logoSrc; // Logo bundled from the frontend project
    watermark.alt = 'Watermark';
    watermark.style.cssText = `
      position: absolute;
      bottom: 60px;
      right: 20px;
      width: 120px;
      height: auto;
      opacity: 0.3;
      pointer-events: none;
      user-select: none;
      z-index: 10;
    `;

    containerRef.current.appendChild(watermark);

    return () => {
      watermark.remove();
    };
  }, [enableProtection]);

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      if (playPromiseRef.current) {
        playPromiseRef.current
          .then(() => {
            if (videoRef.current) {
              videoRef.current.pause();
            }
          })
          .catch(() => {})
          .finally(() => {
            playPromiseRef.current = null;
          });
      } else if (videoRef.current) {
        videoRef.current.pause();
      }
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const current = video.currentTime;
      const total = video.duration;
      setCurrentTime(current);
      
      const watchedPercentage = (current / total) * 100;
      setProgress(watchedPercentage);

      // Save progress to localStorage
      if (strengthName) {
        localStorage.setItem(
          `video_progress_${strengthName}`,
          JSON.stringify({
            progress: watchedPercentage,
            timestamp: Date.now(),
          })
        );
      }

      // Mark as watched when 90% complete
      if (watchedPercentage >= 90 && !hasWatched) {
        setHasWatched(true);
        onComplete?.();
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      
      if (initialProgress > 0) {
        video.currentTime = (initialProgress / 100) * video.duration;
      }
      
      if (autoPlay && isMountedRef.current) {
        playPromiseRef.current = video.play();
        playPromiseRef.current
          .then(() => {
            playPromiseRef.current = null;
          })
          .catch((err) => {
            if (err.name !== 'AbortError') {
              console.log("Autoplay prevented:", err);
            }
            if (isMountedRef.current) {
              setIsPlaying(false);
            }
            playPromiseRef.current = null;
          });
      }
    };

    const handlePlay = () => {
      if (isMountedRef.current) {
        setIsPlaying(true);
      }
    };

    const handlePause = () => {
      if (isMountedRef.current) {
        setIsPlaying(false);
      }
    };

    const handleEnded = () => {
      if (isMountedRef.current) {
        setIsPlaying(false);
        setHasWatched(true);
        onComplete?.();
      }
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
    };
  }, [strengthName, initialProgress, autoPlay, hasWatched, onComplete]);

  const togglePlay = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      playPromiseRef.current = videoRef.current.play();
      playPromiseRef.current
        .then(() => {
          playPromiseRef.current = null;
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            console.error("Play error:", err);
          }
          if (isMountedRef.current) {
            setIsPlaying(false);
          }
          playPromiseRef.current = null;
        });
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleSeek = (value: number[]) => {
    if (videoRef.current) {
      videoRef.current.currentTime = (value[0] / 100) * duration;
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <Card className="overflow-hidden bg-black">
      <div ref={containerRef} className="relative group" style={{ position: 'relative' }}>
        {securityAlert && (
          <Alert variant="destructive" className="absolute top-4 left-4 right-4 z-20">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Security Alert: Screen recording or developer tools detected. Video playback has been paused.
            </AlertDescription>
          </Alert>
        )}

        {loadError && (
          <Alert variant="destructive" className="absolute top-4 left-4 right-4 z-20">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {loadError}
            </AlertDescription>
          </Alert>
        )}

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-white text-sm">Loading protected video...</p>
            </div>
          </div>
        )}

        <video
          ref={videoRef}
          src={blobUrl || undefined}
          className="w-full aspect-video"
          crossOrigin="anonymous"
          playsInline
          controlsList="nodownload"
          disablePictureInPicture
          disableRemotePlayback
          onContextMenu={(e) => e.preventDefault()}
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
        />

        {showControls && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
            <Slider
              value={[currentTime ? (currentTime / duration) * 100 : 0]}
              onValueChange={handleSeek}
              max={100}
              step={0.1}
              className="mb-4"
            />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={togglePlay}
                  className="text-white hover:bg-white/20"
                >
                  {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleMute}
                  className="text-white hover:bg-white/20"
                >
                  {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </Button>

                <span className="text-white text-sm">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFullscreen}
                className="text-white hover:bg-white/20"
              >
                <Maximize size={20} />
              </Button>
            </div>
          </div>
        )}

        {hasWatched && (
          <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm">
            {t('videoPlayer.completed')}
          </div>
        )}
      </div>
    </Card>
  );
};
